import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { Subject, ReplaySubject, Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';

import { App, Roles } from 'app/models';
import { ApiUrl } from 'app/constants/ApiUrls';

@Injectable()
export class AppsService implements OnDestroy {
  readonly appsUrl = `${environment.apiUrl}/api/v2/Apps`;
  readonly applicationsUrl = ApiUrl.apps;
  public selectedApp: App;
  private _apps: ReplaySubject<App[]> = new ReplaySubject<App[]>();
  public apps$: Observable<any> = this._apps.asObservable();
  private _applicationRoles: ReplaySubject<App[]> = new ReplaySubject<App[]>();
  public applicationRoles$: Observable<any> = this._applicationRoles.asObservable();
  private _appRoles: ReplaySubject<Roles[]> = new ReplaySubject<Roles[]>();
  public appRoles$: Observable<any> = this._appRoles.asObservable();
  private onDestroy$ = new Subject<void>();
  public userInitials: string;
  public loading: boolean;
  constructor(
    private _http: HttpClient,
    private _translateService: TranslateService
  ) {
    this.getApps();
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  getAppsCache(): void {
    const apps = JSON.parse(localStorage.getItem('apps'));
    if (apps) {
      this._apps.next(apps);
    } else {
      this.getApps();
    }
  }

  getApps() {
    this._http.get(this.applicationsUrl).pipe(map((apps : App[]) => {apps.forEach(app => {
      if(app.isUserAdmin){
        app['isRefreshRequired'] = false;
      }else{
        app['isRefreshRequired'] = true;
      }
    }); return apps;})).subscribe({
      next:(data) => this.loadApps(data),
      error:(error) => this._handleError(error)
  });
  }

  isSetupRequired(appId) :Observable<boolean>{
    const url = `${this.applicationsUrl}/${appId}/IsSetupRequired`;
    return this._http.get<boolean>(url);
  }

  getApplications(): void {
    const url = `${this.applicationsUrl}/ApplicationRoles`;
    this._http.get<App[]>(url).subscribe({
      next: (data) => this._applicationRoles.next(data),
      error: (error) => this._handleError(error)
    });
  }
  
  loadApps(apps: App[]): void {
    localStorage.setItem('apps', JSON.stringify(apps));
    this._apps.next(apps);
  }

  createApps(id) {
    const post = { clientId: id };
    return this._http.post(this.appsUrl, post);
  }

  postRoles(payload, id) {
    const url = `${this.appsUrl}/${id}/Roles`;
    return this._http.post(url, payload);
  }

  deleteRoles(id, appRoleId) {
    const url = `${this.applicationsUrl}/${id}/Roles/${appRoleId}`;
    return this._http.delete(url);
  }

  private _handleError(error: HttpErrorResponse) {
    let errorMsg: string;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[apps-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[apps-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
    this._apps.error(errorMsg);
  }

  getRoles(appId) {
    this._http.get(`${this.applicationsUrl}/${appId}/Roles`).subscribe({
      next:roles => this._appRoles.next(roles as Roles[]),
      error:error => this._handleError(error)
  });
  }
  getAllAppRoles(appId) {
    return this._http.get<Roles[]>(`${this.applicationsUrl}/${appId}/AllAppRoles`)
  }
  setUpApplication(data:any){
    const url=`${this.applicationsUrl}/Setup`;
    return this._http.post(url,data);
  }
  
}
